import React, { useCallback, useState } from 'react';
import { Tappable } from '@vectord/components';
import { batch, useDispatch, useSelector } from 'react-redux';
import classNames from 'classnames';

import {
  BLACKLIST_ADD_MODAL_STAGES,
  blacklistedMenteeInputErrorSelector,
  setBlacklistedMenteeManageLearner,
  setInputErrors,
  setPersonalisedMenteeEmailersAddModalStage,
  validateBlacklistAddModalErrors,
  validateBlacklistPauseDates,
} from 'features/communicationManagement';
import PersonalisedMenteeEmailersLearnerInput from '../PersonalisedMenteeEmailersLearnerInput';
import PersonalisedMenteeEmailersTimeInput from '../PersonalisedMenteeEmailersTimeInput';
import SectionHeading from 'components/SectionHeading';
import snoozingBell from 'assets/icons/snoozingBell.svg';
import styles from './PersonalisedMenteeEmailersAddLearnerModal.module.scss';

export default function PersonalisedMenteeEmailersAddLearnerModal() {
  const dispatch = useDispatch();
  const inputErrors = useSelector(blacklistedMenteeInputErrorSelector);
  const [endDate, setEndDate] = useState(null);
  const [learnerEmail, setLearnerEmail] = useState('');
  const [startDate, setStartDate] = useState(null);

  const handleEmail = useCallback((e) => {
    setLearnerEmail(e.target.value);
  }, []);

  const handleStartDate = useCallback((date) => {
    setStartDate(date);
  }, []);

  const handleEndDate = useCallback((date) => {
    setEndDate(date);
  }, []);

  const shouldValidateLearnerInput = useCallback(() => {
    const learnerInputErrors = validateBlacklistAddModalErrors(
      learnerEmail,
      startDate,
      endDate
    );

    if (Object.keys(learnerInputErrors).length !== 0) {
      dispatch(setInputErrors(learnerInputErrors));
      return true;
    } else {
      dispatch(setInputErrors(null));
      return false;
    }
  }, [dispatch, endDate, startDate, learnerEmail]);

  const shouldValidateDateInput = useCallback(() => {
    const dateInputErrors = validateBlacklistPauseDates(startDate, endDate);
    if (Object.keys(dateInputErrors).length !== 0) {
      dispatch(setInputErrors(dateInputErrors));
      return true;
    } else {
      dispatch(setInputErrors(null));
      return false;
    }
  }, [dispatch, endDate, startDate]);

  const handleAddClick = useCallback(() => {
    if (shouldValidateLearnerInput() || shouldValidateDateInput()) return;

    const detailUpdate = {
      user_email: learnerEmail,
      start_date: startDate,
      end_date: endDate,
    };
    batch(() => {
      dispatch(setBlacklistedMenteeManageLearner(detailUpdate));
      dispatch(
        setPersonalisedMenteeEmailersAddModalStage(
          BLACKLIST_ADD_MODAL_STAGES.addConfirmation
        )
      );
    });
  }, [
    dispatch,
    endDate,
    learnerEmail,
    startDate,
    shouldValidateDateInput,
    shouldValidateLearnerInput,
  ]);

  return (
    <>
      <SectionHeading
        title={
          <div className={styles.header}>
            <img
              src={snoozingBell}
              className="primary m-r-xs h2"
              alt="icon"
            />
            ADD LEARNER
          </div>
        }
      />
      <PersonalisedMenteeEmailersLearnerInput
        handleChange={handleEmail}
        label="Learner Email:"
        placeholder="Enter Learner Email"
        value={learnerEmail}
        name="email"
        prefixName={false}
        errors={inputErrors}
      />
      <PersonalisedMenteeEmailersTimeInput
        handlePauseStartDateChange={handleStartDate}
        handlePauseEndDateChange={handleEndDate}
        inputErrors={inputErrors}
        pauseStartDate={startDate}
        pauseEndDate={endDate}
      />
      <Tappable
        className={classNames(styles.action, 'p-v-xxs', 'm-t-m')}
        onClick={handleAddClick}
      >
        Add
      </Tappable>
    </>
  );
}
